import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { AccountCard } from "./AccountCard";
import { SplitHeading } from "./Reveal";
import { LAUNCHERS } from "../lib/api";
import { Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SORTS = {
  featured: (a, b) => Number(b.featured) - Number(a.featured) || a.price - b.price,
  "price-asc": (a, b) => a.price - b.price,
  "price-desc": (a, b) => b.price - a.price,
  rank: (a, b) => b.rank - a.rank,
};

export const Catalog = ({ products, loading, onBuy, deliveryTime = "" }) => {
  const [launcher, setLauncher] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products
      .filter((p) => launcher === "all" || p.launcher === launcher)
      .filter((p) => !term || `${p.name} ${p.tagline} ${(p.unlocks ?? []).join(" ")}`.toLowerCase().includes(term))
      .sort(SORTS[sort]);
  }, [products, launcher, q, sort]);

  return (
    <section id="catalog" className="relative mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32" data-testid="catalog-section">
      <SplitHeading eyebrow="02 — The lineup" lines={["Pick your", "account."]} className="text-6xl md:text-9xl" />
      {deliveryTime && (
        <p className="mt-6 inline-flex items-center gap-2 border border-[#a259ff]/40 bg-[#a259ff]/10 px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-200" data-testid="catalog-delivery-time">
          <Clock className="h-4 w-4 text-violet-300" /> Delivery after payment: <span className="text-white">{deliveryTime}</span>
        </p>
      )}

      <div className="mt-12 flex flex-col gap-4 border-y border-white/10 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2" data-testid="launcher-filters">
          {[["all", "All PC"], ...Object.entries(LAUNCHERS)].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setLauncher(id)}
              data-testid={`launcher-filter-${id}`}
              className={`relative rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${launcher === id ? "text-black" : "text-slate-300 hover:text-white"}`}
            >
              {launcher === id && <motion.span layoutId="lf" className="absolute inset-0 rounded-full bg-[#a259ff]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search unlocks, names…"
              data-testid="account-search-input"
              className="h-11 w-full rounded-full border border-white/15 bg-transparent pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-[#a259ff] focus:outline-none sm:w-72"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-full rounded-full border-white/15 bg-transparent text-white sm:w-48" data-testid="account-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0f0f16] text-white">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price-asc">Price: low to high</SelectItem>
              <SelectItem value="price-desc">Price: high to low</SelectItem>
              <SelectItem value="rank">Highest rank</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="account-grid">
        {list.map((p, i) => (
          <AccountCard key={p.id} p={p} i={i} onBuy={onBuy} />
        ))}
      </div>
      {!loading && list.length === 0 && (
        <p className="mt-10 font-mono text-sm uppercase tracking-widest text-slate-400" data-testid="catalog-empty">No accounts match. Try another launcher or search.</p>
      )}
      {loading && <p className="mt-10 font-mono text-sm uppercase tracking-widest text-slate-400">Loading accounts…</p>}
    </section>
  );
};
